import {Button, Flex, Modal, Spin, Typography} from "antd";
import React, {useEffect, useState} from 'react';
import {UploadFile} from "./UploadFile";
import {TakePhoto} from "../TakePhoto";
import {useParams} from "react-router-dom";
import {AttachmentType, UploadAttachment} from "../../../models/Practice/PracticeFIle";
import {PracticesHttpService} from "../../../services/Http/PracticesHttpService";
import {NotificationService} from "../../../services/NotificationService";
import {ScanDocument} from "./ScanDocument";
import {AuthService} from "../../../services/AuthService";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {DocumentDateField} from "./DocumentDateField";
import {PracticeDetail} from "../../../models/Practice/PracticeDetail";

dayjs.extend(customParseFormat);

export interface UploadFilesModalProps{
    practiceDetail: PracticeDetail;
    isModalOpen:boolean
    onClose:()=>void
    onAfterUpload?:()=>void
    attachmentData : UploadAttachment
    isInvoice?: boolean;
}
export function UploadFilesModal(props:UploadFilesModalProps){
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const PREVIMEDICAL_MAX_SIZE_MB = 4;
    const PREVIMEDICAL_MAX_FILE_SIZE = PREVIMEDICAL_MAX_SIZE_MB * 1024 * 1024;
    const [documentDate, setDocumentDate] = useState<dayjs.Dayjs | null>(null);

    const [isLoading, setLoading] = useState(false);

    const insuranceProvider = props.practiceDetail.insuranceProvider;

    // nota, campi presenti solo per insuranceProvider ISP
    const documentDateFrom = dayjs(props.practiceDetail.picMetadata?.orderData?.fieldData['Data ordine'], "MM/DD/YYYY");
    const documentDateTo = dayjs(new Date());

    let {id} = useParams();

    useEffect(() => {
        if (props.isModalOpen) {
            setDocumentDate(null);
        }
    }, [props.isModalOpen]);

    const handleClose = () => {
        props.onClose();
        setErrorMessage(null);
    }

    const onDateChange = (date: string | undefined) => {
        if (!date) {
            setDocumentDate(null);
            return;
        }
        setDocumentDate(dayjs(date, "DD/MM/YYYY"));
    }

    const isValidFileSize = (size: Number) => {
        if (insuranceProvider.integrationCode === 'PREVIMEDICAL' && (size > PREVIMEDICAL_MAX_FILE_SIZE)) {
            return false;
        }
        return true;
    }

    const validExtensions = () => {
        if (insuranceProvider.integrationCode === 'PREVIMEDICAL') {
            return '.pdf,image/jpeg,image/png,image/tiff';
        }
        return '';
    }

    const beforeUpload = (file: File) => {
        if (!isValidFileSize(file.size)) {
            setErrorMessage('Il file è troppo grande. La dimensione massima consentita è di ' + PREVIMEDICAL_MAX_SIZE_MB + ' MB.');
            return false;
        }
        setErrorMessage(null);
        return true;
    };

    const attachmentData: UploadAttachment = {
        ...props.attachmentData,
        ...(documentDate && { documentDate: documentDate.format('YYYY-MM-DD') })
    };

    async function uploadFile(photoDataUri: string, scan: boolean) {
        setLoading(true);
        const response = await fetch(photoDataUri);
        const file = await response.blob();
        if (!isValidFileSize(file.size)) {
            setLoading(false);
            let uploadedSize = (file.size / (1024 * 1024))
            setErrorMessage('Il file immagine è troppo grande (' + Number(uploadedSize).toFixed(2) + ' MB). La dimensione massima consentita è di ' + PREVIMEDICAL_MAX_SIZE_MB + ' MB.');
            return false;
        }

        let data = { ...attachmentData, scan: scan };
        if (scan && data.attachmentType == AttachmentType.INVOICE) {
            data.attachmentType = AttachmentType.DRAFT_INVOICE
        }
        const formData = new FormData();
        formData.append('attachmentData', JSON.stringify(data));
        formData.append('file', file);
        try {
            const response = await PracticesHttpService.uploadPracticeFiles(id, formData);
            const compressed = response.data ? response.data.compressed : false;
            const message = compressed ? "File compresso e salvato correttamente" : "File caricato correttamente";
            NotificationService.getInstance().openNotificationSuccess(message);
            if (props.onAfterUpload) {
                props.onAfterUpload();
            }
            props.onClose();
        } catch (error: any) {
            let message = "Impossibile caricare file";
            if (error.response.status === 400) {
                const data = error.response.data;
                message = data.message;
            }
            NotificationService.getInstance().openNotificationError(message);
        } finally {
            setLoading(false);
        }
    }

    async function onChangeTakePhoto(photoDataUri: string){
        const result = await uploadFile(photoDataUri, false);
        if (props.onAfterUpload) {
            props.onAfterUpload();
        }
        return result;
    }

    async function onChangeScanDocument(photoDataUri: string) {
        const result = await uploadFile(photoDataUri, true);
        if (props.onAfterUpload) {
            props.onAfterUpload();
        }
        return result;
    }

    function onUpload(){
        props.onClose();
        setErrorMessage(null);
    }

    return <>
        <Modal
            width={820}
            closable={true}
            open={props.isModalOpen}
            title={props.isInvoice ? "Caricamento fattura" : "Caricamento documenti"}
            onCancel={handleClose}
            footer={(_, { OkBtn, CancelBtn }) => (
                <>
                    <Button onClick={handleClose} htmlType="reset">Chiudi</Button>
                </>
            )}
        >
            <Spin spinning={isLoading}>
                <Flex vertical gap="middle">
                    {(props.isInvoice && insuranceProvider.integrationCode === 'ISP') &&
                        <Flex justify="center" gap="middle" align="center" style={{ marginTop: '10px' }}>
                            <span>Data Fattura</span>
                            <DocumentDateField required={true} large={true} onChange={onDateChange} value={documentDate}
                                minDate={documentDateFrom} maxDate={documentDateTo} />
                        </Flex>
                    }
                    {!(props.isInvoice && insuranceProvider.integrationCode === 'ISP' && documentDate === null) &&
                    <Flex justify={"space-around"}>
                        <UploadFile id={id} attachmentData={attachmentData}
                                    onUpload={onUpload} beforeUpload={beforeUpload} onAfterUpload={props.onAfterUpload}
                                    acceptTypes={validExtensions()} handleSpin={(value:boolean) => setLoading(value)}></UploadFile>
                        <TakePhoto onChange={onChangeTakePhoto}></TakePhoto>
                        {AuthService.canUseScanner() && <ScanDocument onChange={onChangeScanDocument} />}
                    </Flex>
                    }
                </Flex>
            </Spin>
            {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
        </Modal>
    </>
}